// src/components/network/ForfeoFulfillmentReadinessPanel.tsx
import React from 'react';
import { FaBell, FaCheckCircle, FaClock, FaEnvelope, FaExclamationTriangle, FaQrcode, FaShieldAlt, FaTicketAlt, FaUserCheck, FaWallet } from 'react-icons/fa';

type Props = {
  context?: 'orders' | 'last_call';
  total?: number;
  confirmed?: number;
  completed?: number;
  anomalies?: number;
};

export default function ForfeoFulfillmentReadinessPanel({
  context = 'orders',
  total = 0,
  confirmed = 0,
  completed = 0,
  anomalies = 0,
}: Props) {
  const isLastCall = context === 'last_call';
  const title = isLastCall ? 'Préparation exécution Last Call' : 'Préparation exécution vouchers & pass';
  const subtitle = isLastCall
    ? 'Chaque réservation Last Call doit devenir un pass suivi : confirmation, rappel, présence, no-show et éventuel règlement partenaire.'
    : 'Chaque commande payée doit pouvoir produire un pass, un QR, une preuve d’utilisation et un suivi clair avant le règlement partenaire.';

  return (
    <section className="rounded-[2rem] border border-blue-100 bg-gradient-to-br from-white via-blue-50 to-emerald-50 p-5 shadow-sm md:p-6">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div className="max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/80 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-blue-700">
            <FaShieldAlt /> Forfeo Network Ops
          </div>
          <h3 className="text-2xl font-black tracking-tight text-slate-950 md:text-3xl">{title}</h3>
          <p className="mt-2 text-sm font-medium leading-6 text-slate-600">{subtitle}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:min-w-[520px]">
          <Metric icon={<FaTicketAlt />} label="Total" value={total} tone="slate" />
          <Metric icon={<FaCheckCircle />} label="Confirmé" value={confirmed} tone="blue" />
          <Metric icon={<FaUserCheck />} label="Utilisé" value={completed} tone="emerald" />
          <Metric icon={<FaExclamationTriangle />} label="À vérifier" value={anomalies} tone={anomalies > 0 ? 'red' : 'emerald'} />
        </div>
      </div>
      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Step icon={<FaWallet />} title="Wallet" text="Préparer l’affichage côté app et l’historique client." />
        <Step icon={<FaQrcode />} title="QR / preuve" text="Chaque utilisation doit être traçable et non duplicable." />
        <Step icon={<FaEnvelope />} title="Emails" text="Confirmation, reçu, rappel et support doivent rester alignés." />
        <Step icon={<FaClock />} title="Règlement" text="Le paiement partenaire reste séparé de l’achat client." />
      </div>
      <div className="mt-4 flex items-start gap-3 rounded-3xl border border-amber-100 bg-amber-50 p-4 text-amber-900">
        <FaBell className="mt-1 shrink-0" />
        <p className="text-xs font-bold leading-5">
          Ce lot ne déclenche aucun paiement, ne modifie pas les Edge Functions et ne change pas les statuts existants. Il ajoute seulement une couche de lecture opérationnelle pour préparer le modèle Forfeo Network.
        </p>
      </div>
    </section>
  );
}

function Metric({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: number; tone: string }) {
  const color = tone === 'red' ? 'bg-red-50 text-red-700 border-red-100' : tone === 'emerald' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : tone === 'blue' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-50 text-slate-700 border-slate-100';
  return (
    <div className={`rounded-3xl border p-4 text-center ${color}`}>
      <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-2xl bg-white/70 text-lg">{icon}</div>
      <div className="text-2xl font-black">{Number(value || 0)}</div>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70">{label}</div>
    </div>
  );
}

function Step({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="rounded-3xl border border-white/80 bg-white/80 p-4 shadow-sm">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 text-white">{icon}</div>
      <p className="text-sm font-black text-slate-950">{title}</p>
      <p className="mt-1 text-xs font-medium leading-5 text-slate-500">{text}</p>
    </div>
  );
}
